import { create } from 'zustand';
import * as api from '../components/dropgen/api';
import type { SearchProduct } from '../components/dropgen/api';

export interface Product {
  rank: number;
  product_name: string;
  score: number;
  cost: number;
  margin: number;
  trend: string;
  profit_potential: string;
  estimated_monthly_revenue: string;
  quick_wins: string[];
  image?: string;
  category?: string;
  shipping_days?: number;
  supplier_rating?: number;
  retail_price?: number;
  cj_product_id?: string;
  competition_level?: string;
  demand_score?: number;
  risks?: string[];
  opportunities?: string[];
  full_analysis?: any;
}

export interface ChatMessage {
  id: string;
  sender: 'user' | 'system' | 'dropgen';
  content: string;
  timestamp: Date;
}

export interface Agent {
  name: string;
  status: 'idle' | 'running' | 'completed' | 'error';
  duration?: number;
  startTime?: number;
}

export interface ResearchSession {
  id: string;
  niche: string;
  status: 'idle' | 'researching' | 'completed' | 'error';
  progress: number;
  opportunityScore?: number;
  productsFound?: number;
  avgMargin?: number;
  bestProduct?: string;
}

export interface DataSource {
  name: string;
  status: 'active' | 'syncing' | 'error' | 'inactive';
  lastSync?: Date;
}

export interface ResearchHistoryItem {
  id: string;
  niche: string;
  status: string;
  created_at: string;
  completed_at?: string;
  products_count?: number;
  opportunity_score?: number;
  recommendation?: string;
}

export interface SortConfig {
  column: string;
  direction: 'asc' | 'desc';
}

interface DropGenStore {
  // Current research session
  currentResearch: ResearchSession | null;

  // Research history
  researchHistory: ResearchHistoryItem[];
  isLoadingHistory: boolean;

  // Loading states
  isStartingResearch: boolean;
  isSendingMessage: boolean;
  isLoadingProducts: boolean;
  error: string | null;

  // Products from latest research
  products: Product[];
  selectedProduct: Product | null;

  // Chat messages
  messages: ChatMessage[];

  // Agents status
  agents: Agent[];

  // Data sources
  dataSources: DataSource[];

  // Active view
  activeView: 'search' | 'discover' | 'myproducts' | 'autostore' | 'progress' | 'results' | 'details' | 'store' | 'researchlab';

  // WebSocket connection
  wsConnected: boolean;

  // UI preferences (persisted to localStorage)
  sortConfig: SortConfig;

  // Search state
  searchQuery: string;
  searchProducts: SearchProduct[];
  searchPage: number;
  searchTotal: number;
  searchHasMore: boolean;
  searchLoading: boolean;
  savedProductIds: Set<string>;
  searchSortBy: 'relevance' | 'roi' | 'margin' | 'cost_low' | 'cost_high';

  // Actions
  loadResearchHistory: () => Promise<void>;
  clearResearchHistory: () => Promise<void>;
  deleteResearch: (researchId: string) => Promise<void>;
  loadResearch: (researchId: string) => Promise<void>;
  startResearch: (niche: string) => Promise<void>;
  updateResearch: (updates: Partial<ResearchSession>) => void;
  completeResearch: (products: Product[], score: number) => void;

  addMessage: (sender: 'user' | 'system' | 'dropgen', content: string) => void;
  sendMessage: (message: string) => Promise<void>;
  loadChatHistory: (researchId: string) => Promise<void>;
  clearMessages: () => void;

  updateAgent: (name: string, updates: Partial<Agent>) => void;
  resetAgents: () => void;

  setProducts: (products: Product[]) => void;
  loadProducts: (researchId: string) => Promise<void>;
  selectProduct: (product: Product | null) => void;

  setActiveView: (view: 'search' | 'discover' | 'myproducts' | 'autostore' | 'progress' | 'results' | 'details' | 'store' | 'researchlab') => void;
  setSortConfig: (config: SortConfig) => void;

  updateDataSource: (name: string, updates: Partial<DataSource>) => void;

  setWsConnected: (connected: boolean) => void;
  setError: (error: string | null) => void;

  // Search actions
  setSearchQuery: (query: string) => void;
  setSearchProducts: (products: SearchProduct[], append?: boolean) => void;
  setSearchLoading: (loading: boolean) => void;
  setSearchMeta: (meta: { page: number; total: number; hasMore: boolean }) => void;
  addSavedProductId: (id: string) => void;
  setSearchSortBy: (sortBy: 'relevance' | 'roi' | 'margin' | 'cost_low' | 'cost_high') => void;
  clearSearch: () => void;

  reset: () => void;
}

const initialAgents: Agent[] = [
  { name: 'Market Analyst', status: 'idle' },
  { name: 'Product Scout', status: 'idle' },
  { name: 'Trend Forecaster', status: 'idle' },
  { name: 'Profit Calculator', status: 'idle' },
  { name: 'Synthesis Agent', status: 'idle' },
];

const initialDataSources: DataSource[] = [
  { name: 'Google Trends', status: 'active', lastSync: new Date(Date.now() - 1000 * 60 * 15) },
  { name: 'CJ Affiliate Catalog', status: 'active', lastSync: new Date(Date.now() - 1000 * 60 * 30) },
  { name: 'Social Media Analytics', status: 'active', lastSync: new Date(Date.now() - 1000 * 60 * 45) },
];

// localStorage helpers for persisting UI preferences
const STORAGE_KEY = 'dropgen_preferences';

function loadPreferences(): { sortConfig: SortConfig } {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('[DropGen] Failed to load preferences from localStorage');
  }
  return { sortConfig: { column: 'score', direction: 'desc' } };
}

function savePreferences(prefs: { sortConfig: SortConfig }) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch (e) {
    console.warn('[DropGen] Failed to save preferences to localStorage');
  }
}

const storedPrefs = loadPreferences();

export const useDropGenStore = create<DropGenStore>((set, get) => ({
  currentResearch: null,
  researchHistory: [],
  isLoadingHistory: false,
  products: [],
  selectedProduct: null,
  messages: [
    {
      id: '1',
      sender: 'system',
      content: 'Welcome to DropGen. Enter a niche to start AI-powered product research.',
      timestamp: new Date(),
    },
  ],
  agents: initialAgents,
  dataSources: initialDataSources,
  activeView: 'researchlab',
  wsConnected: false,
  isStartingResearch: false,
  isSendingMessage: false,
  isLoadingProducts: false,
  error: null,
  sortConfig: storedPrefs.sortConfig,
  searchQuery: '',
  searchProducts: [],
  searchPage: 1,
  searchTotal: 0,
  searchHasMore: false,
  searchLoading: false,
  savedProductIds: new Set(),
  searchSortBy: 'relevance',

  loadResearchHistory: async () => {
    try {
      set({ isLoadingHistory: true });
      const response = await api.getResearchHistory();
      set({ researchHistory: response.research, isLoadingHistory: false });
    } catch (error) {
      console.error('[loadResearchHistory] Error:', error);
      set({ isLoadingHistory: false });
    }
  },

  clearResearchHistory: async () => {
    try {
      await api.clearResearchHistory();
      set({ researchHistory: [], currentResearch: null, products: [], selectedProduct: null });
    } catch (error) {
      console.error('[clearResearchHistory] Error:', error);
      set({ isLoadingHistory: false });
    }
  },

  deleteResearch: async (researchId: string) => {
    try {
      await api.deleteResearch(researchId);
      const state = get();
      // Remove from local history
      set({
        researchHistory: state.researchHistory.filter(r => r.id !== researchId),
        // Clear current if it's the deleted one
        currentResearch: state.currentResearch?.id === researchId ? null : state.currentResearch,
        products: state.currentResearch?.id === researchId ? [] : state.products,
        selectedProduct: state.currentResearch?.id === researchId ? null : state.selectedProduct,
      });
    } catch (error) {
      console.error('[deleteResearch] Error:', error);
    }
  },

  loadResearch: async (researchId: string) => {
    try {
      // Clear all state first before loading new research
      set({
        isStartingResearch: true,
        error: null,
        selectedProduct: null,
        products: [],
        activeView: 'progress'
      });

      const research = await api.getResearch(researchId);

      // Map API response to ResearchSession
      const session: ResearchSession = {
        id: research.id,
        niche: research.niche,
        status: research.status === 'completed' ? 'completed' :
                research.status === 'failed' ? 'error' : 'researching',
        progress: research.status === 'completed' ? 100 : 0,
        opportunityScore: research.results?.final_report?.overall_opportunity_score,
        productsFound: research.results?.products?.length,
        avgMargin: research.results?.products?.[0]?.margin,
        bestProduct: research.results?.final_report?.top_products_recommended?.[0]?.product_name,
      };

      set({
        currentResearch: session,
        isStartingResearch: false,
        activeView: session.status === 'completed' ? 'results' : 'progress',
      });

      // Load products if completed
      if (session.status === 'completed') {
        await get().loadProducts(researchId);
      }

      get().addMessage('system', `Loaded research: ${research.niche}`);
    } catch (error) {
      console.error('[loadResearch] Error:', error);
      set({ isStartingResearch: false, error: 'Failed to load research' });
    }
  },

  startResearch: async (niche: string) => {
    try {
      set({ isStartingResearch: true, error: null });

      // Call API to start research
      const response = await api.startResearch(niche);

      set({
        currentResearch: {
          id: response.research_id,
          niche: response.niche,
          status: 'researching',
          progress: 0,
        },
        products: [],
        selectedProduct: null,
        activeView: 'search',
        isStartingResearch: false,
      });

      // Reset agents
      get().resetAgents();

      // Add system message
      get().addMessage('system', `Starting research for niche: ${niche}`);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to start research';
      set({ error: errorMsg, isStartingResearch: false });
      get().addMessage('system', `Error: ${errorMsg}`);
      throw error;
    }
  },

  updateResearch: (updates: Partial<ResearchSession>) => {
    set((state) => ({
      currentResearch: state.currentResearch
        ? { ...state.currentResearch, ...updates }
        : null,
    }));
  },

  completeResearch: (products: Product[], score: number) => {
    const state = get();
    set({
      currentResearch: state.currentResearch
        ? {
            ...state.currentResearch,
            status: 'completed',
            progress: 100,
            opportunityScore: score,
            productsFound: products.length,
            avgMargin: products.reduce((sum, p) => sum + p.margin, 0) / products.length,
            bestProduct: products[0]?.product_name,
          }
        : null,
      products,
      activeView: 'results',
    });

    get().addMessage('system', `Research completed! Found ${products.length} products with opportunity score ${score}/100`);
  },

  addMessage: (sender: 'user' | 'system' | 'dropgen', content: string) => {
    set((state) => ({
      messages: [
        ...state.messages,
        {
          id: `msg-${Date.now()}-${Math.random()}`,
          sender,
          content,
          timestamp: new Date(),
        },
      ],
    }));
  },

  sendMessage: async (message: string) => {
    const state = get();
    if (!state.currentResearch) {
      throw new Error('No active research session');
    }

    try {
      set({ isSendingMessage: true, error: null });

      // Add user message immediately
      get().addMessage('user', message);

      // Send to API
      const response = await api.sendMessage(state.currentResearch.id, message);

      // Add assistant response (also comes via WebSocket, but add here for immediate feedback)
      get().addMessage('dropgen', response.content);

      set({ isSendingMessage: false });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to send message';
      set({ error: errorMsg, isSendingMessage: false });
      get().addMessage('system', `Error: ${errorMsg}`);
      throw error;
    }
  },

  loadChatHistory: async (researchId: string) => {
    try {
      const response = await api.getChatHistory(researchId);

      // Convert API messages to store format
      const messages: ChatMessage[] = response.messages.map((msg) => ({
        id: msg.id || `msg-${Date.now()}-${Math.random()}`,
        sender: msg.role === 'assistant' ? 'dropgen' : msg.role,
        content: msg.content,
        timestamp: msg.created_at ? new Date(msg.created_at) : new Date(),
      }));

      set({ messages });
    } catch (error) {
      console.error('[loadChatHistory] Error:', error);
      get().addMessage('system', 'Failed to load chat history');
    }
  },

  clearMessages: () => {
    set({ messages: [] });
  },

  updateAgent: (name: string, updates: Partial<Agent>) => {
    set((state) => ({
      agents: state.agents.map((agent) =>
        agent.name === name ? { ...agent, ...updates } : agent
      ),
    }));
  },

  resetAgents: () => {
    set({ agents: initialAgents });
  },

  setProducts: (products: Product[]) => {
    set({ products });
  },

  loadProducts: async (researchId: string) => {
    try {
      set({ isLoadingProducts: true, error: null });

      const response = await api.getProducts({ research_id: researchId });

      set({
        products: response.products,
        isLoadingProducts: false,
      });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to load products';
      set({ error: errorMsg, isLoadingProducts: false });
      console.error('[loadProducts] Error:', error);
    }
  },

  selectProduct: (product: Product | null) => {
    set({
      selectedProduct: product,
      activeView: product ? 'details' : 'results',
    });
  },

  setActiveView: (view: 'search' | 'discover' | 'myproducts' | 'autostore' | 'progress' | 'results' | 'details' | 'store' | 'researchlab') => {
    set({ activeView: view });
  },

  setSortConfig: (config: SortConfig) => {
    set({ sortConfig: config });
    // Persist to localStorage
    savePreferences({ sortConfig: config });
  },

  updateDataSource: (name: string, updates: Partial<DataSource>) => {
    set((state) => ({
      dataSources: state.dataSources.map((ds) =>
        ds.name === name ? { ...ds, ...updates } : ds
      ),
    }));
  },

  setWsConnected: (connected: boolean) => {
    set({ wsConnected: connected });
  },

  setError: (error: string | null) => {
    set({ error });
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query });
  },

  setSearchProducts: (products: SearchProduct[], append = false) => {
    set((state) => ({
      searchProducts: append ? [...state.searchProducts, ...products] : products,
    }));
  },

  setSearchLoading: (loading: boolean) => {
    set({ searchLoading: loading });
  },

  setSearchMeta: (meta: { page: number; total: number; hasMore: boolean }) => {
    set({
      searchPage: meta.page,
      searchTotal: meta.total,
      searchHasMore: meta.hasMore,
    });
  },

  addSavedProductId: (id: string) => {
    set((state) => ({
      savedProductIds: new Set([...state.savedProductIds, id]),
    }));
  },

  setSearchSortBy: (sortBy: 'relevance' | 'roi' | 'margin' | 'cost_low' | 'cost_high') => {
    set({ searchSortBy: sortBy });
  },

  clearSearch: () => {
    set({
      searchQuery: '',
      searchProducts: [],
      searchPage: 1,
      searchTotal: 0,
      searchHasMore: false,
      searchLoading: false,
    });
  },

  reset: () => {
    set({
      currentResearch: null,
      products: [],
      selectedProduct: null,
      messages: [
        {
          id: '1',
          sender: 'system',
          content: 'Welcome to DropGen. Enter a niche to start AI-powered product research.',
          timestamp: new Date(),
        },
      ],
      agents: initialAgents,
      activeView: 'researchlab',
      isStartingResearch: false,
      isSendingMessage: false,
      isLoadingProducts: false,
      error: null,
    });
  },
}));
